import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { salesOrdersApi } from "@/services/salesDocumentsApi";
import type { CreateSalesOrderRequest } from "@/types/salesDocuments";

// Query key factory
const salesOrderKeys = {
  all: ["salesOrders"] as const,
  lists: () => [...salesOrderKeys.all, "list"] as const,
  list: (filters?: any) => [...salesOrderKeys.lists(), filters] as const,
  details: () => [...salesOrderKeys.all, "detail"] as const,
  detail: (id: string) => [...salesOrderKeys.details(), id] as const,
  deliveryOrders: (id: string) =>
    [...salesOrderKeys.detail(id), "deliveryOrders"] as const,
};

// Get all sales orders
export const useSalesOrders = (filters?: {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: salesOrderKeys.list(filters),
    queryFn: () => salesOrdersApi.getAll(filters),
  });
};

// Get single sales order
export const useSalesOrder = (id: string | undefined) => {
  return useQuery({
    queryKey: salesOrderKeys.detail(id || ""),
    queryFn: () => salesOrdersApi.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Create sales order
export const useCreateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSalesOrderRequest) => salesOrdersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
    },
  });
};

// Update sales order
export const useUpdateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateSalesOrderRequest>;
    }) => salesOrdersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: salesOrderKeys.detail(variables.id),
      });
    },
  });
};

// Delete sales order
export const useDeleteSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => salesOrdersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
    },
  });
};

// Update sales order status
export const useUpdateSalesOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      salesOrdersApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: salesOrderKeys.detail(variables.id),
      });
    },
  });
};

// Clone sales order
export const useCloneSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => salesOrdersApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
    },
  });
};

// Send sales order emails to buyers
export const useSendSalesOrderEmail = () => {
  return useMutation({
    mutationFn: (ids: string[]) => salesOrdersApi.sendEmail(ids),
  });
};

// Get delivery orders for a sales order
export const useSalesOrderDeliveryOrders = (id: string | undefined) => {
  return useQuery({
    queryKey: salesOrderKeys.deliveryOrders(id || ""),
    queryFn: () => salesOrdersApi.getDeliveryOrders(id!),
    enabled: !!id,
  });
};

// Hook to use all sales order operations
export const useSalesOrderOperations = () => {
  const createSalesOrder = useCreateSalesOrder();
  const updateSalesOrder = useUpdateSalesOrder();
  const deleteSalesOrder = useDeleteSalesOrder();
  const updateStatus = useUpdateSalesOrderStatus();
  const cloneSalesOrder = useCloneSalesOrder();

  return {
    createSalesOrder,
    updateSalesOrder,
    deleteSalesOrder,
    updateStatus,
    cloneSalesOrder,
    isLoading:
      createSalesOrder.isPending ||
      updateSalesOrder.isPending ||
      deleteSalesOrder.isPending ||
      updateStatus.isPending ||
      cloneSalesOrder.isPending,
  };
};
